import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { MapPin, Phone, Mail, Clock, Instagram, Youtube } from 'lucide-react';
import CareerModal from '../CareerModal';

const Footer = () => {
  const { clinicInfo } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const currentYear = new Date().getFullYear();
  const [isCareerModalOpen, setIsCareerModalOpen] = useState(false);

  const footerLinks = {
    services: [
      { label: language === 'de' ? 'Haartransplantation' : 'Hair Transplant', href: '/', isRoute: true },
      { label: language === 'de' ? 'Barthaartransplantation' : 'Beard Transplant', href: '/bart', isRoute: true },
      { label: language === 'de' ? 'Augenbrauentransplantation' : 'Eyebrow Transplant', href: '/augenbrauen', isRoute: true },
      { label: language === 'de' ? 'Kostenrechner' : 'Price Calculator', href: '/preisrechner', isRoute: true },
    ],
    company: [
      { label: t('footer.about'), href: '#expertise' },
      { label: t('team.sectionTitle'), href: '#team' },
      { label: language === 'de' ? 'Klinik' : 'Clinic', href: '#klinik' },
      { label: language === 'de' ? 'Partner werden' : 'Become a Partner', href: '/partner', isRoute: true },
      { label: language === 'de' ? 'Karriere' : 'Careers', onClick: () => setIsCareerModalOpen(true) },
    ],
    legal: [
      { label: t('footer.imprint'), href: '/impressum', isRoute: true },
      { label: t('footer.privacy'), href: '/datenschutz', isRoute: true },
      { label: t('footer.terms'), href: '/agb', isRoute: true },
    ],
  };

  return (
    <footer className={`transition-colors duration-300 ${
      isDark ? 'bg-[#0f0f0f] border-t border-white/5' : 'bg-gray-50 border-t border-gray-200'
    }`}>
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand & Contact */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_dion-hair/artifacts/cm40t14d_DionHairClinic_Logo.svg"
                alt="Dion Hair Clinic Logo - Haartransplantation Mönchengladbach NRW"
                className={`h-16 w-auto transition-all duration-300 ${isDark ? 'brightness-0 invert' : ''}`}
              />
            </div>
            <p className={`text-sm mb-6 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('footer.aboutText')}
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/dionhairclinic"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                  isDark 
                    ? 'bg-white/5 hover:bg-[#7BA7C2]/20 text-gray-400 hover:text-[#7BA7C2]' 
                    : 'bg-gray-100 hover:bg-[#5A8BA6]/10 text-gray-500 hover:text-[#5A8BA6]'
                }`}
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@dionhairclinic"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                  isDark 
                    ? 'bg-white/5 hover:bg-[#7BA7C2]/20 text-gray-400 hover:text-[#7BA7C2]' 
                    : 'bg-gray-100 hover:bg-[#5A8BA6]/10 text-gray-500 hover:text-[#5A8BA6]'
                }`}
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className={`font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {language === 'de' ? 'Behandlungen' : 'Treatments'}
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  {link.isRoute ? (
                    <Link
                      to={link.href}
                      className={`text-sm transition-colors duration-300 ${
                        isDark ? 'text-gray-400 hover:text-[#7BA7C2]' : 'text-gray-600 hover:text-[#5A8BA6]'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className={`text-sm transition-colors duration-300 ${
                        isDark ? 'text-gray-400 hover:text-[#7BA7C2]' : 'text-gray-600 hover:text-[#5A8BA6]'
                      }`}
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className={`font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {language === 'de' ? 'Unternehmen' : 'Company'}
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  {link.onClick ? (
                    <button
                      onClick={link.onClick}
                      className={`text-sm transition-colors duration-300 ${
                        isDark ? 'text-gray-400 hover:text-[#7BA7C2]' : 'text-gray-600 hover:text-[#5A8BA6]'
                      }`}
                    >
                      {link.label}
                    </button>
                  ) : (
                    <a
                      href={link.href}
                      className={`text-sm transition-colors duration-300 ${
                        isDark ? 'text-gray-400 hover:text-[#7BA7C2]' : 'text-gray-600 hover:text-[#5A8BA6]'
                      }`}
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className={`font-semibold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('nav.contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {clinicInfo.address.street}<br />
                  {clinicInfo.address.city}
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <a 
                  href={`tel:${clinicInfo.phone.replace(/\s/g, '')}`} 
                  className={`text-sm transition-colors duration-300 ${
                    isDark ? 'text-gray-400 hover:text-[#7BA7C2]' : 'text-gray-600 hover:text-[#5A8BA6]'
                  }`}
                >
                  {clinicInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <a 
                  href={`mailto:${clinicInfo.email}`} 
                  className={`text-sm transition-colors duration-300 ${
                    isDark ? 'text-gray-400 hover:text-[#7BA7C2]' : 'text-gray-600 hover:text-[#5A8BA6]'
                  }`}
                >
                  {clinicInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {clinicInfo.openingHours.weekdays}<br />
                  {clinicInfo.openingHours.saturday}
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className={`border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              © {currentYear} {clinicInfo.name}. {t('footer.allRightsReserved')}
            </p>
            <div className="flex gap-6">
              {footerLinks.legal.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className={`text-sm transition-colors duration-300 ${
                    isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Career Modal */}
      <CareerModal 
        isOpen={isCareerModalOpen} 
        onClose={() => setIsCareerModalOpen(false)} 
      />
    </footer>
  );
};

export default Footer;